/** Helpers for White Glove project list cards and filters (dashboard). */

const CONTENT_ORDER_TYPES = ['silo_order', 'quick_content_order']

/** Single source for legend, filter tabs, sidebar dots, cards, and site layout header. */
export const WHITE_GLOVE_SITE_STATUS = {
  action: {
    key: 'action',
    label: 'Need your action',
    tabLabel: 'Need your action',
    color: '#e07b39',
  },
  progress: {
    key: 'progress',
    label: "We're working on it",
    tabLabel: 'In progress',
    color: '#4a90d9',
  },
  review: {
    key: 'review',
    label: 'Ready for review',
    tabLabel: 'Ready for review',
    color: '#f05a28',
  },
  complete: {
    key: 'complete',
    label: 'Complete',
    tabLabel: 'Complete',
    color: '#4caf50',
  },
  issues: {
    key: 'issues',
    label: 'Issue',
    tabLabel: 'Issues',
    color: '#e53935',
  },
  none: {
    key: 'none',
    label: 'No orders yet',
    tabLabel: '',
    color: '#e07b39',
  },
}

/** Order shown in the dashboard legend (matches tab semantics + colors). */
export const WHITE_GLOVE_LEGEND_ITEMS = [
  WHITE_GLOVE_SITE_STATUS.action,
  WHITE_GLOVE_SITE_STATUS.progress,
  WHITE_GLOVE_SITE_STATUS.review,
  WHITE_GLOVE_SITE_STATUS.complete,
  WHITE_GLOVE_SITE_STATUS.issues,
]

const WG_DOT_FALLBACK_HEX = '9e9e9e'

function parseWhiteGloveDotHex (hex) {
  let h = String(hex || '').trim()
  if (!h) h = WG_DOT_FALLBACK_HEX
  if (h[0] === '#') h = h.slice(1)
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('')
  }
  if (h.length !== 6 || !/^[0-9a-fA-F]+$/.test(h)) {
    h = WG_DOT_FALLBACK_HEX
  }
  return {
    full: `#${h}`,
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  }
}

/**
 * Inline styles for `.wg-status-dot`: fill plus a soft glow using the same hue at 30% opacity
 * (e.g. blue `box-shadow: 0px 0px 6px 0px #316BFF4D`).
 */
export function whiteGloveStatusDotStyle (color) {
  const { full, r, g, b } = parseWhiteGloveDotHex(color)
  return {
    backgroundColor: full,
    boxShadow: `0px 0px 6px 0px rgba(${r}, ${g}, ${b}, 0.3)`,
  }
}

/**
 * Normalizes GET /api/white-glove-orders/ payload (supports legacy responses without siteProjectStatuses).
 */
export function parseWhiteGloveOrdersResponse (response) {
  const payload = (response && response.data) || {}
  return {
    orders: Array.isArray(payload.data) ? payload.data : [],
    siteProjectStatuses:
      payload.siteProjectStatuses && typeof payload.siteProjectStatuses === 'object'
        ? payload.siteProjectStatuses
        : {},
  }
}

export function fulfillmentStatus (o) {
  return o.fulfillement_status || o.fulfillementStatus || ''
}

export function ordersForSite (orders, siteId) {
  const id = Number(siteId)
  return (orders || []).filter((o) => Number(o.site_id) === id)
}

function statusEntryForSite (siteId, siteProjectStatuses) {
  if (!siteProjectStatuses || typeof siteProjectStatuses !== 'object') return null
  const a = siteProjectStatuses[String(siteId)]
  if (a) return a
  return siteProjectStatuses[Number(siteId)] || null
}

export function auditHasCompletedOnboarding (audit) {
  if (!audit || audit.type !== 'audit_order') return false
  if (typeof audit.hasCompletedOnboarding === 'boolean') return audit.hasCompletedOnboarding
  // POP Authority lets the user skip the Google step (gscSkippedAt) instead of importing data
  const gsc = Number(audit.hasRetrievedFreshGSCData) === 1 || Boolean(audit.gscSkippedAt)
  const form = audit.status === 'form_submitted'
  const domain =
    Boolean(audit.hasAssociatedDomainLevelCampaign) ||
    Number(audit.has_ignored_associated_campaign_requirement) === 1
  return form && domain && gsc
}

function legacyGetWhiteGloveSiteTags (siteId, orders) {
  const tags = new Set()
  const rows = ordersForSite(orders, siteId)

  if (rows.length === 0) {
    tags.add('action')
    return tags
  }

  const audits = rows.filter((o) => o.type === 'audit_order')
  const contentOrders = rows.filter((o) => CONTENT_ORDER_TYPES.includes(o.type))

  for (const audit of audits) {
    if (!auditHasCompletedOnboarding(audit)) {
      tags.add('action')
    }
  }

  for (const o of rows) {
    const st = fulfillmentStatus(o)
    if (st === 'in_progress') {
      tags.add('progress')
    }
    if (o.type === 'audit_order' && st === 'pending' && auditHasCompletedOnboarding(o)) {
      tags.add('progress')
    }
    if (CONTENT_ORDER_TYPES.includes(o.type) && st === 'pending') {
      tags.add('progress')
    }
  }

  const allCompleted =
    rows.length > 0 && rows.every((o) => fulfillmentStatus(o) === 'completed')
  if (allCompleted) {
    tags.add('complete')
  }

  if (
    contentOrders.some((o) => fulfillmentStatus(o) === 'completed') &&
    !allCompleted
  ) {
    tags.add('review')
  }

  return tags
}

/**
 * Which filter tabs a site belongs to (a site may match several when using legacy inference).
 */
export function getWhiteGloveSiteTags (siteId, orders, siteProjectStatuses = null) {
  const api = statusEntryForSite(siteId, siteProjectStatuses)
  if (api && api.filter_bucket) {
    const b = api.filter_bucket === 'none' ? 'action' : api.filter_bucket
    return new Set([b])
  }
  return legacyGetWhiteGloveSiteTags(siteId, orders)
}

/**
 * Priority status key for dot + headline label (action → progress → review → complete).
 */
export function getWhiteGloveSiteStatusPriorityKey (siteId, orders, siteProjectStatuses = null) {
  const api = statusEntryForSite(siteId, siteProjectStatuses)
  if (api && api.filter_bucket) {
    const b = api.filter_bucket === 'none' ? 'none' : api.filter_bucket
    return b
  }
  const rows = ordersForSite(orders, siteId)
  if (rows.length === 0) return 'none'
  const tags = legacyGetWhiteGloveSiteTags(siteId, orders)
  if (tags.has('action')) return 'action'
  if (tags.has('progress')) return 'progress'
  if (tags.has('review')) return 'review'
  if (tags.has('complete')) return 'complete'
  return 'progress'
}

/**
 * Single line + badge for SiteCardWhiteGlove, menu dots, layout header.
 */
export function getWhiteGloveSiteCardInsight (siteId, orders, siteProjectStatuses = null) {
  const rows = ordersForSite(orders, siteId)
  // canceled rows (POP Authority booster orders) are not active work; White Glove rows never carry it
  const activeOrdersCount = rows.filter(
    (o) => fulfillmentStatus(o) !== 'completed' && o.order_status !== 'canceled'
  ).length
  const api = statusEntryForSite(siteId, siteProjectStatuses)

  if (api) {
    const bucket = api.filter_bucket === 'none' ? 'action' : (api.filter_bucket || 'action')
    const meta = WHITE_GLOVE_SITE_STATUS[bucket] || WHITE_GLOVE_SITE_STATUS.action
    const showBadge = Boolean(api.show_badge)
    const cardLine = api.card_headline || meta.label
    const dot = api.dot_color || meta.color
    return {
      statusKey: bucket,
      projectStatusKey: api.status_key || '',
      statusLabel: cardLine,
      dotColor: dot,
      badgeLabel: showBadge ? (api.badge_label || '') : '',
      showBadge,
      activeOrdersCount,
      overviewTitle: api.overview_title || '',
      overviewBody: api.overview_body || '',
      primaryCtaLabel: api.primary_cta_label || '',
      primaryCtaRoute: api.primary_cta_route || '',
      timelineLabel: api.timeline_label || '',
      filterBucket: bucket,
      // POP Authority: live package tag for the project card ("Growth · Month 2"), '' otherwise
      packageLabel: api.package_tag || '',
    }
  }

  if (rows.length === 0) {
    const z = WHITE_GLOVE_SITE_STATUS.none
    return {
      statusKey: z.key,
      projectStatusKey: '',
      statusLabel: z.label,
      dotColor: z.color,
      badgeLabel: '',
      showBadge: false,
      activeOrdersCount: 0,
      overviewTitle: '',
      overviewBody: '',
      primaryCtaLabel: '',
      primaryCtaRoute: '',
      timelineLabel: '',
      filterBucket: z.key,
      packageLabel: '',
    }
  }

  const priorityKey = getWhiteGloveSiteStatusPriorityKey(siteId, orders, null)
  const meta = WHITE_GLOVE_SITE_STATUS[priorityKey] || WHITE_GLOVE_SITE_STATUS.complete
  const audits = rows.filter((o) => o.type === 'audit_order')
  let setupDone = false
  if (audits.length) {
    const sorted = [...audits].sort((a, b) => {
      const ta = a.order_date_timestamp || 0
      const tb = b.order_date_timestamp || 0
      return tb - ta
    })
    setupDone = auditHasCompletedOnboarding(sorted[0])
  }

  return {
    statusKey: meta.key,
    projectStatusKey: '',
    statusLabel: meta.label,
    dotColor: meta.color,
    badgeLabel: setupDone ? 'Setup complete' : 'Finish setup',
    showBadge: true,
    activeOrdersCount,
    overviewTitle: '',
    overviewBody: '',
    primaryCtaLabel: '',
    primaryCtaRoute: '',
    timelineLabel: '',
    filterBucket: meta.key,
    packageLabel: '',
  }
}

export function countSitesByWhiteGloveTag (sites, orders, siteProjectStatuses = null) {
  const counts = {
    all: sites.length,
    action: 0,
    progress: 0,
    review: 0,
    complete: 0,
    issues: 0,
  }
  for (const site of sites) {
    const tags = getWhiteGloveSiteTags(site.id, orders, siteProjectStatuses)
    if (tags.has('action')) counts.action += 1
    if (tags.has('progress')) counts.progress += 1
    if (tags.has('review')) counts.review += 1
    if (tags.has('complete')) counts.complete += 1
    if (tags.has('issues')) counts.issues += 1
  }
  return counts
}
